// dentlab/src/store/RecycleBin/recycle-bin.store.ts
import { StateCreator } from "zustand";
import { supabase } from "@/lib/supabase";
import {
	RecycleBinFilesViewType,
	SupabaseViewEnum,
	SupabaseTableEnum,
	FileWithShare,
	JobDocumentEntityType,
	RecycleBinJobDocumentsViewType,
} from "@/lib/supabase/supabaseTypes";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "../Central/selectors";
import { useJobStore } from "../Jobs";
import { useCentralStore } from "../Central";
import { isInvoiceFile } from "./utils";
import { documentDrivesJobStatus } from "../Jobs/utils";
import { JobDocumentTypeEnum } from "../../pages/job-page/job-document/job-document.types";
import { JobStatusEnum } from "@/lib/types/job";

export interface RecycleBinStoreSlice {
	recycleBinFiles: RecycleBinFilesViewType[];
	recycleBinJobDocuments: RecycleBinJobDocumentsViewType[];
	fetchRecycleBinFiles: (organizationId?: string) => Promise<void>;
	fetchRecycleBinDocuments: (organizationId?: string) => Promise<void>;
	deleteFileTemporarily: (
		file: RecycleBinFilesViewType | FileWithShare
	) => Promise<boolean>;
	restoreFile: (
		file: RecycleBinFilesViewType | FileWithShare
	) => Promise<boolean>;
	restoreJobDocument: (
		jobDocument: RecycleBinJobDocumentsViewType
	) => Promise<boolean>;
	deleteJobDocumentTemporarily: (
		jobDocument: JobDocumentEntityType
	) => Promise<boolean>;
}

const fetchJobFiles = useJobStore.getState().fetchJobFiles;
const fetchJobDocument = useJobStore.getState().fetchJobDocument;

export const createRecycleBinStore: StateCreator<RecycleBinStoreSlice> = (
	set,
	get
) => ({
	recycleBinFiles: [],
	recycleBinJobDocuments: [],
	fetchRecycleBinFiles: async (organizationId?: string) => {
		let orgId = organizationId;

		if (!organizationId) {
			orgId = useCentralStore.getState().organization?.id ?? undefined;
		}

		if (!orgId) {
			Logger.error("No organization ID provided");
			return;
		}

		const { data, error } = await supabase
			.from(SupabaseViewEnum.RECYCLE_BIN_FILES)
			.select("*")
			.not("deleted_at", "is", null)
			.eq("organization_id", orgId);

		if (error) {
			Logger.error(error, {}, "Error fetching deleted files");
			return;
		}

		set({
			recycleBinFiles: data || [],
		});
	},
	deleteFileTemporarily: async (
		file: RecycleBinFilesViewType | FileWithShare
	) => {
		if (isInvoiceFile(file)) {
			showNotification({
				message:
					"Diese Datei ist eine Rechnung und kann nicht gelöscht werden",
				type: "error",
			});
			return false;
		}
		if (!file.id) {
			showNotification({
				message: "Keine Datei-ID angegeben",
				type: "error",
			});
			return false;
		}

		const { data: activeShares, error: sharesError } = await supabase
			.from(SupabaseTableEnum.SHARED)
			.select("id")
			.eq("file_id", file.id)
			.is("shared_until", null);

		if (sharesError) {
			Logger.error(sharesError, {}, "Error fetching file shares");
			return false;
		}

		if (activeShares && activeShares.length > 0) {
			if (
				!confirm(
					"Die Datei wurde bereits geteilt. Beim verschieben in den Papierkorb wird die Dateifreigabe aufgehoben. Fortfahren?"
				)
			) {
				return false;
			}
		}

		const { error: deleteError } = await supabase
			.from(SupabaseTableEnum.FILES)
			.update({
				deleted_at: new Date().toISOString(),
			})
			.eq("id", file.id);

		if (deleteError) {
			Logger.error(deleteError, {}, "Error deleting file");
			showNotification({
				message: "Fehler beim löschen der Datei",
				type: "error",
			});
			return false;
		}

		await get().fetchRecycleBinFiles();
		if (file.job_document_id) {
			await fetchJobDocument(file.job_document_id);
		} else {
			if (file.job_id) {
				await fetchJobFiles(file.job_id);
			} else {
				showNotification({
					message:
						"Laden sie die Seite neu, um alle Dateien zu sehen",
					type: "warning",
				});
			}
		}

		return true;
	},
	restoreFile: async (file: RecycleBinFilesViewType | FileWithShare) => {
		if (!file.id) {
			showNotification({
				message: "Keine Datei-ID angegeben",
				type: "error",
			});
			return false;
		}

		const oldRecycleBinFiles = get().recycleBinFiles;

		set((state) => ({
			recycleBinFiles: state.recycleBinFiles.filter(
				(recycleBinFile) => recycleBinFile.id !== file.id
			),
		}));

		const { error } = await supabase
			.from(SupabaseTableEnum.FILES)
			.update({ deleted_at: null })
			.eq("id", file.id);

		if (error) {
			Logger.error(error, {}, "Error restoring file");
			showNotification({
				message: "Fehler beim Wiederherstellen der Datei",
				type: "error",
			});
			set({ recycleBinFiles: oldRecycleBinFiles });
			return false;
		}

		await get().fetchRecycleBinFiles();
		if (file.job_document_id) {
			await fetchJobDocument(file.job_document_id);
		} else {
			if (file.job_id) {
				await fetchJobFiles(file.job_id);
			} else {
				showNotification({
					message:
						"Laden sie die Seite neu, um alle Dateien zu sehen",
					type: "warning",
				});
			}
		}

		return true;
	},
	fetchRecycleBinDocuments: async (organizationId?: string) => {
		const orgId =
			organizationId || useCentralStore.getState().organization?.id;
		if (!orgId) {
			Logger.error("No organization ID provided");
			return;
		}
		const { data, error } = await supabase
			.from(SupabaseViewEnum.RECYCLE_BIN_JOB_DOCUMENTS)
			.select("*")
			.not("deleted_at", "is", null)
			.eq("organization_id", orgId);

		if (error) {
			Logger.error(error, {}, "Error fetching recycle bin job documents");
			return;
		}

		set({
			recycleBinJobDocuments: data || [],
		});
	},
	restoreJobDocument: async (jobDocument: RecycleBinJobDocumentsViewType) => {
		if (!jobDocument.id) {
			showNotification({
				message: "Keine Job-Dokument-ID angegeben",
				type: "error",
			});
			return false;
		}

		if (
			jobDocument.type === JobDocumentTypeEnum.DeliveryNote &&
			jobDocument.job_id
		) {
			const { count, error } = await supabase
				.from(SupabaseTableEnum.JOB_DOCUMENTS)
				.select("id", { count: "exact", head: true })
				.eq("job_id", jobDocument.job_id)
				.eq("type", JobDocumentTypeEnum.DeliveryNote)
				.is("deleted_at", null)
				.neq("status", JobStatusEnum.ARCHIVED);

			if (error) {
				Logger.error(error, {}, "Error checking active delivery notes");
				showNotification({
					message: "Fehler beim Wiederherstellen des Job-Dokuments",
					type: "error",
				});
				return false;
			}
			if (count) {
				showNotification({
					message:
						"Der Auftrag enthält bereits einen aktiven Lieferschein",
					type: "error",
				});
				return false;
			}
		}

		const { error } = await supabase
			.from(SupabaseTableEnum.JOB_DOCUMENTS)
			.update({ deleted_at: null })
			.eq("id", jobDocument.id);

		if (error) {
			Logger.error(error, {}, "Error restoring job document");
			showNotification({
				message: "Fehler beim Wiederherstellen des Job-Dokuments",
				type: "error",
			});
			return false;
		}

		await get().fetchRecycleBinDocuments();
		if (jobDocument.id) {
			await fetchJobDocument(jobDocument.id);
		}
		if (documentDrivesJobStatus(jobDocument.type) && jobDocument.job_id) {
			await useJobStore.getState().syncJobStatus(jobDocument.job_id);
		}
		return true;
	},
	deleteJobDocumentTemporarily: async (
		jobDocument: JobDocumentEntityType
	) => {
		if (!jobDocument.id) {
			showNotification({
				message: "Keine Job-Dokument-ID angegeben",
				type: "error",
			});
			return false;
		}

		const {
			isDeleteJobDocumentAllowed,
			deleteJobDocumentLocally,
			syncJobStatus,
		} = useJobStore.getState();
		if (!isDeleteJobDocumentAllowed(jobDocument.id).isAllowed) {
			showNotification({
				message: "Das Dokument darf nicht gelöscht werden!",
				type: "warning",
			});
			return false;
		}
		const { error } = await supabase
			.from(SupabaseTableEnum.JOB_DOCUMENTS)
			.update({ deleted_at: new Date().toISOString() })
			.eq("id", jobDocument.id);

		if (error) {
			Logger.error(error, {}, "Error deleting job document");
			showNotification({
				message: "Fehler beim Löschen des Job-Dokuments",
				type: "error",
			});
			return false;
		}

		await get().fetchRecycleBinDocuments();
		deleteJobDocumentLocally(jobDocument.id);
		if (documentDrivesJobStatus(jobDocument.type) && jobDocument.job_id) {
			await syncJobStatus(jobDocument.job_id);
		}
		return true;
	},
});
